/**
 * Shared SpiderChart calculation utilities
 * Used by both web SpiderChart and PDF SpiderChart to avoid duplication
 */

export interface SpiderChartPoint {
  x: number;
  y: number;
}

export interface SpiderChartConstants {
  CHART_CENTER_X: number;
  CHART_CENTER_Y: number;
  OUTER_RADIUS: number;
  MIN_RADIUS: number;
  LABEL_RADIUS: number;
  SCORE_RADIUS: number;
}

export const SPIDER_CHART_CONSTANTS: SpiderChartConstants = {
  CHART_CENTER_X: 120,
  CHART_CENTER_Y: 120,
  OUTER_RADIUS: 70,
  MIN_RADIUS: 4,
  LABEL_RADIUS: 95,
  SCORE_RADIUS: 80,
};

/**
 * Calculate point for polygon based on skill score (scale-aware)
 */
export const calculateSpiderChartPoint = (
  index: number,
  value: number,
  skillsLength: number,
  scale: number,
  constants: SpiderChartConstants = SPIDER_CHART_CONSTANTS
): SpiderChartPoint => {
  // Normalize value from 1-scale to 0-1 (subtract 1 then divide by (scale-1))
  const normalizedValue = Math.max(0, Math.min(1, (value - 1) / (scale - 1)));
  
  // Calculate radius with minimum kernel at level 1
  const radius = constants.MIN_RADIUS + normalizedValue * (constants.OUTER_RADIUS - constants.MIN_RADIUS);
  
  // Calculate angle based on position in the array
  const angleInRadians = (Math.PI * 2 * index / skillsLength) - Math.PI / 2;
  
  // Calculate the point coordinates
  const x = constants.CHART_CENTER_X + radius * Math.cos(angleInRadians);
  const y = constants.CHART_CENTER_Y + radius * Math.sin(angleInRadians);
  
  return { x, y };
};

/**
 * Generate polygon points string for data visualization
 */
export const generateSpiderChartPolygonPoints = (
  skills: { name: string; value: number }[],
  scale: number,
  constants: SpiderChartConstants = SPIDER_CHART_CONSTANTS
): string => {
  return skills.map((skill, index) => {
    const point = calculateSpiderChartPoint(index, skill.value, skills.length, scale, constants);
    return `${point.x},${point.y}`;
  }).join(' ');
};

/**
 * Generate background polygons for scale levels
 */
export const generateSpiderChartBackgroundPolygons = (
  skillsLength: number,
  scale: number,
  constants: SpiderChartConstants = SPIDER_CHART_CONSTANTS
): Array<{ points: string; level: number }> => {
  // Create background polygons for levels 2 to scale (level 1 is the inner kernel)
  const levels = Array.from({ length: scale - 1 }, (_, i) => i + 2);
  return levels.map(level => {
    const ratio = (level - 1) / (scale - 1);
    const points = Array.from({ length: skillsLength }, (_, index) => {
      const radius = constants.MIN_RADIUS + ratio * (constants.OUTER_RADIUS - constants.MIN_RADIUS);
      const angleInRadians = (Math.PI * 2 * index / skillsLength) - Math.PI / 2;
      const x = constants.CHART_CENTER_X + radius * Math.cos(angleInRadians);
      const y = constants.CHART_CENTER_Y + radius * Math.sin(angleInRadians);
      return `${x},${y}`;
    }).join(' ');
    
    return { points, level };
  });
};

/**
 * Calculate label position for spider chart
 */
export interface LabelPosition {
  x: number;
  y: number;
  dx: number;
  dy: number;
  textAnchor: "start" | "middle" | "end";
  alignmentBaseline: "auto" | "baseline" | "middle" | "hanging";
}

export const calculateSpiderChartLabelPosition = (
  index: number,
  skillsLength: number,
  isLabel: boolean = true,
  constants: SpiderChartConstants = SPIDER_CHART_CONSTANTS
): LabelPosition => {
  const angle = (Math.PI * 2 * index / skillsLength) - Math.PI / 2;
  const radius = isLabel ? constants.LABEL_RADIUS : constants.SCORE_RADIUS;
  const x = constants.CHART_CENTER_X + radius * Math.cos(angle);
  const y = constants.CHART_CENTER_Y + radius * Math.sin(angle);
  
  // Adjust text anchor and alignment based on position
  let textAnchor: "start" | "middle" | "end" = "middle";
  let alignmentBaseline: "auto" | "baseline" | "middle" | "hanging" = "middle";
  let dx = 0;
  let dy = 0;
  
  // Top position
  if (angle <= -Math.PI/3 && angle >= -2*Math.PI/3) {
    textAnchor = "middle";
    alignmentBaseline = isLabel ? "hanging" : "baseline";
    dy = -8;
  } 
  // Right position
  else if (angle > -Math.PI/3 && angle < Math.PI/3) {
    textAnchor = "start";
    alignmentBaseline = "middle";
    dx = 8;
  } 
  // Bottom position
  else if (angle >= Math.PI/3 && angle <= 2*Math.PI/3) {
    textAnchor = "middle";
    alignmentBaseline = "hanging";
    dy = 8;
  }
  // Left position
  else {
    textAnchor = "end";
    alignmentBaseline = "middle";
    dx = -8;
  }
  
  return { x, y, dx, dy, textAnchor, alignmentBaseline };
};

/**
 * Generate level 1 polygon points (minimum kernel)
 */
export const generateSpiderChartLevel1Polygon = (
  skillsLength: number,
  constants: SpiderChartConstants = SPIDER_CHART_CONSTANTS
): string => {
  return Array.from({ length: skillsLength }, (_, index) => {
    const angleInRadians = (Math.PI * 2 * index / skillsLength) - Math.PI / 2;
    const x = constants.CHART_CENTER_X + constants.MIN_RADIUS * Math.cos(angleInRadians);
    const y = constants.CHART_CENTER_Y + constants.MIN_RADIUS * Math.sin(angleInRadians);
    return `${x},${y}`;
  }).join(' ');
};

/**
 * Calculate axis line coordinates
 */
export interface AxisLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export const calculateSpiderChartAxisLine = (
  index: number,
  skillsLength: number,
  constants: SpiderChartConstants = SPIDER_CHART_CONSTANTS
): AxisLine => {
  const angle = (Math.PI * 2 * index / skillsLength) - Math.PI / 2;
  return {
    x1: constants.CHART_CENTER_X + constants.MIN_RADIUS * Math.cos(angle),
    y1: constants.CHART_CENTER_Y + constants.MIN_RADIUS * Math.sin(angle),
    x2: constants.CHART_CENTER_X + constants.OUTER_RADIUS * Math.cos(angle),
    y2: constants.CHART_CENTER_Y + constants.OUTER_RADIUS * Math.sin(angle),
  };
};

